// SSE Service to manage the EventSource connection
import { useEventsStore } from '../stores/events-store'
import apiClient from './api-client'

/** Shape returned by GET /api/Event/sync?since= */
interface SyncEvent {
  id: number
  eventType: string
  data: string // JSON-serialized payload
  createdAt: string // ISO date
}

export class SSEService {
  private eventSource: EventSource | null = null
  private lastHeartbeatAt: number = Date.now()
  private heartbeatTimeoutMs = 65000 // slightly more than 2x the 30s server heartbeat

  /** Timestamp of the last SSE event of any kind (heartbeat, message, etc.) */
  private lastEventAt: number = Date.now()

  private visibilityHandler: (() => void) | null = null

  /** setInterval-based watchdog that detects sleep/wake gaps */
  private watchdogTimerId: ReturnType<typeof setInterval> | null = null
  private lastWatchdogTickAt: number = Date.now()
  private readonly watchdogIntervalMs = 5_000 // tick every 5 s
  private readonly watchdogDriftThresholdMs = 15_000 // >15 s drift → device was sleeping

  /** Guard to prevent concurrent sync + reconnect */
  private isSyncing = false

  constructor() {}

  // Initialize the connection to the SSE endpoint
  connect() {
    if (this.eventSource) {
      this.disconnect()
    }

    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const sseUrl = `${baseUrl}/api/Event/stream`

    const now = Date.now()
    this.lastHeartbeatAt = now
    this.lastEventAt = now
    this.lastWatchdogTickAt = now

    this.eventSource = new EventSource(sseUrl)
    const eventsStore = useEventsStore()

    // ── Visibility-change handler (covers tab switch / minimize) ──
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.checkAndRecover()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)

    // ── Watchdog timer (covers device sleep/wake while tab stays visible) ──
    this.startWatchdog()

    // Set up event listeners
    this.eventSource.onopen = () => {
      console.log('SSE connection established')
      this.touchEvent()
      eventsStore.setConnectionStatus(true)
    }

    // Handle connection/welcome event
    this.eventSource.addEventListener('connected', (event) => {
      this.touchEvent()
      try {
        const data = JSON.parse(event.data)
        console.log('SSE connected with client ID:', data.clientId)
        eventsStore.addEvent({
          type: 'connected',
          data: data,
          timestamp: new Date(),
        })
      } catch (error) {
        console.error('Error parsing connected event data', error)
      }
    })

    // Handle heartbeat events
    this.eventSource.addEventListener('heartbeat', () => {
      console.log('SSE heartbeat received')
      this.lastHeartbeatAt = Date.now()
      this.touchEvent()
    })

    // Handle message events (the default event type)
    this.eventSource.addEventListener('message', (event) => {
      this.touchEvent()
      try {
        const data = JSON.parse(event.data)
        eventsStore.addEvent({
          type: 'message',
          data: data,
          timestamp: new Date(),
        })
      } catch (error) {
        console.error('Error parsing message event data', error)
      }
    })

    this.eventSource.addEventListener('new_message', (event) => {
      this.touchEvent()
      try {
        const data = JSON.parse(event.data)
        console.log('SSE new_message event received:', data)
        eventsStore.addEvent({
          type: 'new_message',
          data: data,
          timestamp: new Date(),
        })
      } catch (error) {
        console.error('Error parsing new_message event data', error)
      }
    })

    // Handle chat closed events
    this.eventSource.addEventListener('chat_closed', (event) => {
      this.touchEvent()
      try {
        const data = JSON.parse(event.data)
        console.log('SSE chat_closed event received:', data)
        eventsStore.addEvent({
          type: 'chat_closed',
          data: data,
          timestamp: new Date(),
        })
      } catch (error) {
        console.error('Error parsing chat_closed event data', error)
      }
    })

    // Handle chat reopened events
    this.eventSource.addEventListener('chat_reopened', (event) => {
      this.touchEvent()
      try {
        const data = JSON.parse(event.data)
        console.log('SSE chat_reopened event received:', data)
        eventsStore.addEvent({
          type: 'chat_reopened',
          data: data,
          timestamp: new Date(),
        })
      } catch (error) {
        console.error('Error parsing chat_reopened event data', error)
      }
    })

    // Handle chat assigned events
    this.eventSource.addEventListener('chat_assigned', (event) => {
      this.touchEvent()
      try {
        const data = JSON.parse(event.data)
        console.log('SSE chat_assigned event received:', data)
        eventsStore.addEvent({
          type: 'chat_assigned',
          data: data,
          timestamp: new Date(),
        })
      } catch (error) {
        console.error('Error parsing chat_assigned event data', error)
      }
    })

    // Handle broadcast job completed events
    this.eventSource.addEventListener('broadcast_job_completed', (event) => {
      this.touchEvent()
      try {
        const data = JSON.parse(event.data)
        console.log('SSE broadcast_job_completed event received:', data)
        eventsStore.addEvent({
          type: 'broadcast_job_completed',
          data: data,
          timestamp: new Date(),
        })
      } catch (error) {
        console.error('Error parsing broadcast_job_completed event data', error)
      }
    })

    // Handle replay-complete event (after reconnect with Last-Event-ID)
    this.eventSource.addEventListener('replay-complete', (event) => {
      this.touchEvent()
      try {
        const data = JSON.parse(event.data)
        console.log(`SSE replay-complete: ${data.replayed} events replayed`)
      } catch (error) {
        console.error('Error parsing replay-complete event data', error)
      }
    })

    // Global error handler for EventSource
    this.eventSource.onerror = () => {
      eventsStore.setConnectionStatus(false)
    }

    // Set up custom event listeners from the controller
    this.setupCustomEventListeners()
  }

  // Set up listeners for custom event types
  private setupCustomEventListeners() {
    if (!this.eventSource) return

    // people_changed: an admin merged/unmerged contacts; the chat pages refetch their lists
    const customEventTypes = ['notification', 'update', 'alert', 'people_changed']
    const eventsStore = useEventsStore()

    customEventTypes.forEach((eventType) => {
      this.eventSource?.addEventListener(eventType, (event) => {
        this.touchEvent()
        try {
          const data = JSON.parse(event.data)
          eventsStore.addEvent({
            type: eventType,
            data: data,
            timestamp: new Date(),
          })
        } catch (error) {
          console.error(`Error parsing ${eventType} event data`, error)
        }
      })
    })
  }

  // Disconnect from the SSE endpoint
  disconnect() {
    this.stopWatchdog()
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
      const eventsStore = useEventsStore()
      eventsStore.setConnectionStatus(false)
      console.log('SSE connection closed')
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Sleep / wake detection & stale-connection recovery
  // ────────────────────────────────────────────────────────────────

  /** Update the "last event received" timestamp on every SSE event */
  private touchEvent() {
    this.lastEventAt = Date.now()
  }

  /** Returns true when the SSE connection should be considered stale */
  private isConnectionStale(): boolean {
    const msSinceLastEvent = Date.now() - this.lastEventAt
    return (
      !this.eventSource ||
      this.eventSource.readyState === EventSource.CLOSED ||
      msSinceLastEvent > this.heartbeatTimeoutMs
    )
  }

  // ── Watchdog ──────────────────────────────────────────────────

  private startWatchdog() {
    this.stopWatchdog()
    this.lastWatchdogTickAt = Date.now()

    this.watchdogTimerId = setInterval(() => {
      const now = Date.now()
      const drift = now - this.lastWatchdogTickAt - this.watchdogIntervalMs

      this.lastWatchdogTickAt = now

      // Always perform a health check. This catches stale connections even when
      // there is no visibility change and no large timer drift.
      this.checkAndRecover()

      // A large drift means the browser timer was frozen (device sleep).
      if (drift > this.watchdogDriftThresholdMs) {
        console.log(`Watchdog detected ${Math.round(drift / 1000)}s timer drift – device likely slept. Checking SSE…`)
        this.checkAndRecover()
      }
    }, this.watchdogIntervalMs)
  }

  private stopWatchdog() {
    if (this.watchdogTimerId !== null) {
      clearInterval(this.watchdogTimerId)
      this.watchdogTimerId = null
    }
  }

  // ── Core recovery flow ────────────────────────────────────────

  /**
   * Called by both the visibilitychange handler and the watchdog.
   * 1. Checks whether the connection is stale.
   * 2. Calls the sync endpoint for fast event replay.
   * 3. Emits "connection_stale" so the UI can do a full data reload.
   * 4. Reconnects the SSE stream.
   */
  private async checkAndRecover() {
    if (!this.isConnectionStale()) return
    if (this.isSyncing) return
    this.isSyncing = true

    const sinceTimestamp = new Date(this.lastEventAt)

    console.log(
      `SSE connection stale (last event ${Math.round(
        (Date.now() - this.lastEventAt) / 1000,
      )}s ago). Starting recovery…`,
    )
    try {
      // Step 1 – Fast sync: fetch missed events via REST
      await this.syncMissedEvents(sinceTimestamp)
    } catch (err) {
      console.warn('Fast event sync failed, falling back to full reload only:', err)
    }

    // Step 2 – Notify the UI to do a full data reload (chat list + current messages)
    try {
      const eventsStore = useEventsStore()
      eventsStore.addEvent({
        type: 'connection_stale',
        data: { since: sinceTimestamp.toISOString() },
        timestamp: new Date(),
      })
    } catch (err) {
      console.error('Error emitting connection_stale event:', err)
    }

    // Step 3 – Reconnect the SSE stream (server will also replay via Last-Event-ID)
    this.isSyncing = false
    this.connect()
  }

  private formatLocalDateForQuery(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0')

    const offsetMinutes = -date.getTimezoneOffset()
    const offsetSign = offsetMinutes >= 0 ? '+' : '-'
    const absoluteOffsetMinutes = Math.abs(offsetMinutes)
    const offsetHours = String(Math.floor(absoluteOffsetMinutes / 60)).padStart(2, '0')
    const offsetMins = String(absoluteOffsetMinutes % 60).padStart(2, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetSign}${offsetHours}:${offsetMins}`
  }

  /**
   * Fetches missed SSE events from the REST sync endpoint and replays them
   * through the events store so that all existing listeners are notified.
   */
  private async syncMissedEvents(since: Date): Promise<void> {
    const sinceQuery = encodeURIComponent(this.formatLocalDateForQuery(since))
    const events = await apiClient.get<SyncEvent[]>(`/api/Event/sync?since=${sinceQuery}`)

    if (!events || events.length === 0) {
      console.log('Sync: no missed events to replay')
      return
    }

    console.log(`Sync: replaying ${events.length} missed events`)
    const eventsStore = useEventsStore()

    for (const evt of events) {
      try {
        const data = JSON.parse(evt.data)
        eventsStore.addEvent({
          type: evt.eventType,
          data: data,
          timestamp: new Date(evt.createdAt),
        })
      } catch (err) {
        console.error(`Sync: failed to replay event ${evt.id} (${evt.eventType}):`, err)
      }
    }
  }
}

export const sseService = new SSEService()
